'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { authClient } from '@bip/api-client';
import { LoginModal } from '../../_components/LoginModal';

interface SportsRedirectGateProps {
  /** Raw `?redirectUrl=…` value read by the server component from its
   *  `searchParams` prop and passed straight through. We deliberately
   *  do NOT call `useSearchParams()` here — that hook's timing on
   *  hydration was the source of the "modal sometimes shows" bug. */
  redirectUrl?: string;
}

/**
 * Handle the `?redirectUrl=…` query parameter on the Sports landing page.
 *
 * Behaviour:
 *   • No `redirectUrl` in URL → renders nothing, never touches /me.
 *   • `redirectUrl` present, user logged in → push to that URL immediately.
 *   • `redirectUrl` present, user logged out → open the LoginModal. The
 *     modal's existing success handler does `window.location.reload()`,
 *     which re-mounts this gate with the same `redirectUrl` still in the
 *     URL — the auth check now succeeds and the redirect fires.
 *
 * Why a separate component rather than baking this into `page.tsx`:
 *   • `page.tsx` is a server component (`async function`). `useEffect`,
 *     `useSearchParams`, and the LoginModal all need a client boundary.
 *   • Keeping the gate self-contained means the rest of the sports
 *     landing page remains SSR-rendered for SEO.
 *
 * Safety rails:
 *   • Same-origin only. Both relative paths (`/manage-bookings`) AND
 *     absolute URLs whose origin matches `window.location.origin`
 *     (`http://localhost:8087/manage-bookings` when running locally) are
 *     accepted; everything else is silently dropped to prevent
 *     open-redirect attacks where a malicious link could ship a victim
 *     offsite after they log in.
 *   • `actedRef` blocks double-firing under React 19 / StrictMode
 *     double-mount.
 */
function normaliseRedirectTarget(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Protocol-relative (`//evil.com/foo`) is always rejected — it
  // sidesteps the origin check by reusing the current scheme.
  if (trimmed.startsWith('//')) return null;

  // Plain relative path — must start with a single `/`.
  if (trimmed.startsWith('/')) return trimmed;

  // Absolute URL — accept only if it parses successfully AND its
  // origin equals the page's origin. We compare against
  // `window.location.origin` rather than a hard-coded value so the
  // gate works on every environment (localhost, sand, prod) without
  // configuration. SSR fallback: when there's no window (shouldn't
  // happen — this component is `'use client'`), reject defensively.
  if (typeof window === 'undefined') return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.origin !== window.location.origin) return null;
    // Convert to relative form so `router.push` does a client-side
    // navigation rather than a full reload.
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function SportsRedirectGate({ redirectUrl }: SportsRedirectGateProps) {
  const router = useRouter();
  const rawRedirectUrl = redirectUrl ?? null;

  // Normalised target lives in state so it can be computed from
  // `window.location.origin` (only available client-side) without
  // hydration-mismatch warnings. `target` is null until the effect
  // resolves it; we use it both to decide whether to mount the modal
  // and to know what to push to after login.
  const [target, setTarget] = useState<string | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const actedRef = useRef(false);

  useEffect(() => {
    const resolved = normaliseRedirectTarget(rawRedirectUrl);
    setTarget(resolved);
    if (!resolved) return;
    if (actedRef.current) return;
    actedRef.current = true;

    let cancelled = false;
    authClient
      .getMe()
      .then((res) => {
        if (cancelled) return;
        const isLoggedIn = res?.message === 'success';
        if (isLoggedIn) {
          router.push(resolved);
        } else {
          setLoginOpen(true);
        }
      })
      .catch(() => {
        // Network error / 401 / etc. — treat as logged out.
        if (!cancelled) setLoginOpen(true);
      });

    return () => {
      cancelled = true;
    };
  }, [rawRedirectUrl, router]);

  // Don't render the modal slot until we know we have a usable target.
  // Stops the modal from ever opening for unsafe/offsite redirectUrls.
  if (!target) return null;

  return (
    <LoginModal
      isOpen={loginOpen}
      onClose={() => setLoginOpen(false)}
      onReopen={() => setLoginOpen(true)}
    />
  );
}
